"use client";

import clsx from "clsx";
import Image from "next/image";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  WhatsAppConnectionStatus,
  disconnectWhatsApp,
  getWhatsAppStatus,
  onboardWhatsApp,
  setWhatsAppAssistant,
} from "@/actions/integrations/whatsapp";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import Select from "@/components/library/Select";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {useAlert} from "@/hooks/useAlert";
import {useAssistants} from "@/hooks/useAssistants";
import {
  WhatsAppSignupResult,
  useWhatsAppEmbeddedSignup,
} from "@/hooks/useWhatsAppEmbeddedSignup";
import DisconnectModal from "./DisconnectModal";

export default function WhatsAppIntegrationCard() {
  const {t} = useTranslation("partners");
  const {setAlert} = useAlert();
  const [status, setStatus] = useState<WhatsAppConnectionStatus>({
    connected: false,
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string>("");
  const [isSavingAssistant, setIsSavingAssistant] = useState(false);

  const {isLoadingAssistants, assistants} = useAssistants({
    autoFetch: true,
    fetchAll: true,
  });

  const assistantOptions = assistants.map((a) => ({
    label: a.specialty ? a.name + " - " + a.specialty : a.name,
    value: a.id,
  }));

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const result = await getWhatsAppStatus();
        setStatus(result);
        if (result.assistantId) {
          setSelectedAssistantId(result.assistantId);
        }
      } catch {
        console.error("Failed to fetch WhatsApp status");
      } finally {
        setStatusLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleSignupSuccess = useCallback(
    async (result: WhatsAppSignupResult) => {
      setIsConnecting(true);
      try {
        const response = await onboardWhatsApp(result);
        if (response.success) {
          setAlert({status: "success", message: t("whatsapp.successConnected")});
          const freshStatus = await getWhatsAppStatus();
          setStatus(freshStatus);
          if (freshStatus.assistantId) {
            setSelectedAssistantId(freshStatus.assistantId);
          }
        } else {
          setAlert({status: "error", message: response.error || t("whatsapp.errorConnect")});
        }
      } catch {
        setAlert({status: "error", message: t("whatsapp.errorConnect")});
      } finally {
        setIsConnecting(false);
      }
    },
    [setAlert, t]
  );

  const handleSignupCancel = useCallback(() => {
    setAlert({status: "info", message: t("whatsapp.signupCancelled")});
  }, [setAlert, t]);

  const {launchSignup, isLoading: isSDKLoading} =
    useWhatsAppEmbeddedSignup(handleSignupSuccess, handleSignupCancel);

  const handleDisconnect = useCallback(async () => {
    setIsDisconnectModalOpen(false);
    setIsDisconnecting(true);
    try {
      const result = await disconnectWhatsApp();
      if (result.success) {
        setAlert({status: "success", message: t("whatsapp.successDisconnected")});
        setStatus({connected: false});
      } else {
        setAlert({status: "error", message: result.error || t("whatsapp.errorDisconnect")});
      }
    } catch {
      setAlert({status: "error", message: t("whatsapp.errorDisconnect")});
    } finally {
      setIsDisconnecting(false);
    }
  }, [setAlert, t]);

  const handleSaveAssistant = useCallback(async () => {
    if (!selectedAssistantId) return;
    setIsSavingAssistant(true);
    try {
      const result = await setWhatsAppAssistant(selectedAssistantId);
      if (result.success) {
        setAlert({status: "success", message: t("whatsapp.successAssistantUpdated")});
        setStatus((prev) => ({...prev, assistantId: selectedAssistantId}));
        setIsSettingsOpen(false);
      } else {
        setAlert({status: "error", message: result.error || t("whatsapp.errorAssistantUpdate")});
      }
    } catch {
      setAlert({status: "error", message: t("whatsapp.errorAssistantUpdate")});
    } finally {
      setIsSavingAssistant(false);
    }
  }, [selectedAssistantId, setAlert, t]);

  const isActionLoading = isConnecting || isSDKLoading || isDisconnecting;

  return (
    <>
      <Card
        className={clsx(
          "relative",
          "flex",
          "flex-col",
          "shadow-none",
          "border-2",
          "border-gray-300",
          "dark:border-gray-700",
          "w-full",
          "min-w-sm"
        )}
      >
        <CardHeader className={clsx("flex", "flex-col", "gap-2")}>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 overflow-hidden">
              <Image
                src="/icons/integrations/whatsapp-color-svgrepo-com.svg"
                alt="WhatsApp"
                width={28}
                height={28}
                className="w-7 h-7"
              />
            </div>
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">{t("whatsapp.title")}</h3>
                {status.connected && (
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-600 dark:text-green-400 dark:border-green-400"
                  >
                    {t("whatsapp.connected")}
                  </Badge>
                )}
              </div>
            </div>
            {status.connected && (
              <button
                type="button"
                onClick={() => setIsSettingsOpen(true)}
                className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors"
                aria-label={t("whatsapp.settingsAriaLabel")}
              >
                <Icon icon="gear" className="w-4 h-4" />
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className={clsx("flex", "flex-col", "flex-1")}>
          <CardContentBody
            statusLoading={statusLoading}
            connected={status.connected}
            displayPhoneNumber={status.displayPhoneNumber}
            t={t}
          />
        </CardContent>
        <CardFooter>
          {status.connected ? (
            <Button
              variant="destructive"
              onClick={() => setIsDisconnectModalOpen(true)}
              disabled={isActionLoading}
              className="w-full"
            >
              {isDisconnecting ? (
                <Icon
                  icon="spinner"
                  size={ICON_SIZES.SM}
                  className="animate-spin mr-2"
                />
              ) : null}
              {t("whatsapp.disconnect")}
            </Button>
          ) : (
            <Button
              onClick={launchSignup}
              disabled={isActionLoading}
              className="w-full"
            >
              {isActionLoading ? (
                <Icon
                  icon="spinner"
                  size={ICON_SIZES.SM}
                  className="animate-spin mr-2"
                />
              ) : (
                <Image
                  src="/icons/integrations/whatsapp-color-svgrepo-com.svg"
                  alt=""
                  width={16}
                  height={16}
                  className="w-4 h-4 mr-2"
                />
              )}
              {isActionLoading ? t("whatsapp.connecting") : t("whatsapp.connectButton")}
            </Button>
          )}
        </CardFooter>
      </Card>

      <DisconnectModal
        show={isDisconnectModalOpen}
        onClose={() => setIsDisconnectModalOpen(false)}
        onConfirm={handleDisconnect}
        integrationName={t("whatsapp.title")}
      />

      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("whatsapp.settingsTitle")}</DialogTitle>
            <DialogDescription>
              {t("whatsapp.settingsDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {isLoadingAssistants ? (
              <p className="text-sm text-gray-500">{t("whatsapp.loadingAssistants")}</p>
            ) : (
              <Select
                label={t("whatsapp.assistantLabel")}
                options={assistantOptions}
                value={selectedAssistantId}
                placeHolder={t("whatsapp.assistantPlaceholder")}
                tooltipText={t("whatsapp.assistantTooltip")}
                onChange={(e: React.SyntheticEvent) => {
                  const target = e.target as HTMLSelectElement;
                  setSelectedAssistantId(target.value);
                }}
              />
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSettingsOpen(false)}
              disabled={isSavingAssistant}
            >
              {t("whatsapp.cancel")}
            </Button>
            <Button
              onClick={handleSaveAssistant}
              disabled={!selectedAssistantId || isSavingAssistant}
            >
              {isSavingAssistant ? (
                <Icon
                  icon="spinner"
                  size={ICON_SIZES.SM}
                  className="animate-spin mr-2"
                />
              ) : null}
              {t("whatsapp.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CardContentBody({
  statusLoading,
  connected,
  displayPhoneNumber,
  t,
}: {
  statusLoading: boolean;
  connected: boolean;
  displayPhoneNumber?: string;
  t: (key: string, options?: Record<string, string>) => string;
}) {
  if (statusLoading) {
    return <p className="text-600 text-sm">{t("whatsapp.loading")}</p>;
  }

  if (connected) {
    return (
      <div className="flex flex-col gap-1 text-sm text-600">
        <p>{t("whatsapp.connectedDescription")}</p>
        {displayPhoneNumber && (
          <p className="text-xs mt-1">
            {t("whatsapp.phone", {phoneNumber: displayPhoneNumber})}
          </p>
        )}
      </div>
    );
  }

  return (
    <p className="text-sm">{t("whatsapp.disconnectedDescription")}</p>
  );
}

